import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Modal } from '../ui/Modal';
import {
  aggregateAlertItems,
  aggregatePurchaseItems,
  buildPurchasePeriodLabels,
  fetchAccountPurchaseSummary,
  isAlertItem,
  monthQty,
  monthSortKey,
  sortPurchaseRows,
  excludeHiddenPurchaseIpgs,
  splitPrimaryBackupItems,
  type AccountPurchaseItem,
  type AccountPurchaseSummary,
  type PurchasePeriodLabels,
  type PurchaseSortKey,
} from '../../lib/accountPurchaseHistory';

type PurchaseSubTab = 'overview' | 'prices' | 'alerts';

interface AccountPurchasePanelProps {
  motherCode: string;
  companyName?: string;
}

function formatQty(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString();
}

function formatPrice(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'SGD',
    maximumFractionDigits: 2,
  });
}

function statusLabel(status: AccountPurchaseItem['status']): string {
  if (status === 'stopped') return 'Stopped';
  if (status === 'declining') return 'Declining';
  return 'OK';
}

function trendLabel(trend: AccountPurchaseItem['month_trend']): string {
  if (trend === 'up') return 'Up';
  if (trend === 'down') return 'Down';
  if (trend === 'new') return 'New';
  return 'Flat';
}

function SortTh({
  label,
  sortKey,
  activeKey,
  dir,
  numeric,
  onSort,
  title,
  className,
}: {
  label: string;
  sortKey: PurchaseSortKey;
  activeKey: PurchaseSortKey;
  dir: 'asc' | 'desc';
  numeric?: boolean;
  onSort: (key: PurchaseSortKey) => void;
  title?: string;
  className?: string;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={[
        'account-purchase__th--sort',
        numeric ? 'account-purchase__num' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={title}
    >
      <button
        type="button"
        className="account-purchase__sort-btn"
        onClick={() => onSort(sortKey)}
        title={title}
      >
        <span>{label}</span>
        <span className="account-purchase__sort-ind" aria-hidden>
          {active ? (dir === 'asc' ? '▲' : '▼') : ''}
        </span>
      </button>
    </th>
  );
}

function QtyCells({
  row,
  periods,
}: {
  row: AccountPurchaseItem;
  periods: PurchasePeriodLabels;
}) {
  return (
    <>
      {periods.monthLabels.map((_, index) => {
        const future = index + 1 > periods.currentMonth;
        const qty = monthQty(row, index);
        return (
          <td
            key={`m-${index}`}
            className={[
              'account-purchase__num',
              index + 1 === periods.currentMonth ? 'account-purchase__col-current' : '',
              future ? 'account-purchase__col-future' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {future ? '—' : formatQty(qty)}
          </td>
        );
      })}
      <td className="account-purchase__num account-purchase__col-split">{formatQty(row.qty_ytd)}</td>
      <td>
        <span className={`account-purchase__trend account-purchase__trend--${row.month_trend}`}>
          {trendLabel(row.month_trend)}
        </span>
      </td>
      <td>
        <span className={`account-purchase__status account-purchase__status--${row.status}`}>
          {statusLabel(row.status)}
        </span>
      </td>
    </>
  );
}

function QtySortHeaders({
  sortKey,
  sortDir,
  onSort,
  periods,
}: {
  sortKey: PurchaseSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: PurchaseSortKey) => void;
  periods: PurchasePeriodLabels;
}) {
  return (
    <>
      {periods.monthLabels.map((label, index) => (
        <SortTh
          key={`mh-${index}`}
          label={label}
          sortKey={monthSortKey(index)}
          activeKey={sortKey}
          dir={sortDir}
          onSort={onSort}
          numeric
          title={`${label} ${periods.year}${
            index + 1 === periods.currentMonth ? ' (MTD)' : index + 1 > periods.currentMonth ? ' (future)' : ''
          }`}
          className={index + 1 === periods.currentMonth ? 'account-purchase__col-current' : undefined}
        />
      ))}
      <SortTh
        label="YTD"
        sortKey="qty_ytd"
        activeKey={sortKey}
        dir={sortDir}
        onSort={onSort}
        numeric
        title={`${periods.year} year to date`}
        className="account-purchase__col-split"
      />
      <SortTh label="Trend" sortKey="month_trend" activeKey={sortKey} dir={sortDir} onSort={onSort} />
      <SortTh label="Status" sortKey="status" activeKey={sortKey} dir={sortDir} onSort={onSort} />
    </>
  );
}

function QtyTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  periods,
  alerts,
  allItems,
  onOpenOutlets,
}: {
  rows: AccountPurchaseItem[];
  sortKey: PurchaseSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: PurchaseSortKey) => void;
  periods: PurchasePeriodLabels;
  alerts?: boolean;
  allItems?: AccountPurchaseItem[];
  onOpenOutlets?: (item: AccountPurchaseItem) => void;
}) {
  if (rows.length === 0) {
    return <p className="account-purchase__empty">No items in this view.</p>;
  }

  return (
    <div
      className={[
        'account-purchase__table-wrap',
        alerts ? 'account-purchase__table-wrap--alerts' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <table
        className={[
          'account-purchase__table',
          'account-purchase__table--qty',
          alerts ? 'account-purchase__table--alerts' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <colgroup>
          <col className="account-purchase__col-item" />
          <col className="account-purchase__col-desc" />
          <col className="account-purchase__col-uom" />
          {periods.monthLabels.map((_, index) => (
            <col key={`cq-${index}`} className="account-purchase__col-qty" />
          ))}
          <col className="account-purchase__col-qty" />
          <col className="account-purchase__col-trend" />
          <col className="account-purchase__col-status" />
        </colgroup>
        <thead>
          <tr>
            <SortTh label="Item No." sortKey="item_no" activeKey={sortKey} dir={sortDir} onSort={onSort} />
            <SortTh
              label="Description"
              sortKey="description"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
            />
            <SortTh label="UOM" sortKey="base_uom" activeKey={sortKey} dir={sortDir} onSort={onSort} />
            <QtySortHeaders
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              periods={periods}
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const outletCount =
              alerts && allItems
                ? allItems.filter((i) => i.item_no === row.item_no && isAlertItem(i)).length
                : 0;
            return (
              <tr
                key={row.item_no}
                className={[
                  alerts ? 'account-purchase__row--clickable' : '',
                  row.status === 'stopped'
                    ? 'account-purchase__row--stopped'
                    : row.status === 'declining'
                      ? 'account-purchase__row--declining'
                      : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDoubleClick={
                  alerts && onOpenOutlets ? () => onOpenOutlets(row) : undefined
                }
                title={alerts ? 'Double-click to open outlets' : undefined}
              >
                <td>
                  <div className="account-purchase__item-cell">
                    <span className="account-purchase__mono">{row.item_no}</span>
                    {outletCount > 0 ? (
                      <span className="account-purchase__outlet-count">{outletCount}</span>
                    ) : null}
                  </div>
                </td>
                <td className="account-purchase__desc">{row.description}</td>
                <td className="account-purchase__uom">{row.base_uom || '—'}</td>
                <QtyCells row={row} periods={periods} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatStock(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return formatQty(value);
}

function formatTa(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function PricesTable({
  rows,
  sortKey,
  sortDir,
  onSort,
}: {
  rows: AccountPurchaseItem[];
  sortKey: PurchaseSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: PurchaseSortKey) => void;
}) {
  if (rows.length === 0) {
    return <p className="account-purchase__empty">No items in this view.</p>;
  }

  return (
    <div className="account-purchase__table-wrap">
      <table className="account-purchase__table account-purchase__table--prices">
        <thead>
          <tr>
            <SortTh
              label="IPG"
              sortKey="ipg"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
              title="Inventory Posting Group"
            />
            <SortTh label="Item No." sortKey="item_no" activeKey={sortKey} dir={sortDir} onSort={onSort} />
            <SortTh
              label="Description"
              sortKey="description"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
            />
            <SortTh
              label="Unit Price"
              sortKey="unit_price"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
              numeric
            />
            <SortTh label="uom" sortKey="base_uom" activeKey={sortKey} dir={sortDir} onSort={onSort} />
            <SortTh
              label="Inventory"
              sortKey="inventory"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
              numeric
            />
            <SortTh
              label="FSM"
              sortKey="fsm"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
              numeric
              title="Forecast Sales Manual"
            />
            <SortTh
              label="TA"
              sortKey="ta"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
              numeric
              title="Turn Around (Inventory ÷ FSM, months of cover)"
            />
            <SortTh
              label="packing"
              sortKey="packing"
              activeKey={sortKey}
              dir={sortDir}
              onSort={onSort}
              title="Std Ctn Packing"
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.item_no}>
              <td>{row.ipg || '—'}</td>
              <td className="account-purchase__mono">{row.item_no}</td>
              <td>{row.description}</td>
              <td className="account-purchase__num account-purchase__price">{formatPrice(row.unit_price)}</td>
              <td>{row.base_uom || '—'}</td>
              <td className="account-purchase__num">{formatStock(row.inventory)}</td>
              <td className="account-purchase__num">{formatStock(row.fsm)}</td>
              <td className="account-purchase__num">{formatTa(row.ta)}</td>
              <td>{row.packing || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BackupSection({
  children,
  count,
}: {
  children: ReactNode;
  count: number;
}) {
  if (count === 0) return null;
  return (
    <section className="account-purchase__backup" aria-label="Backup item codes">
      <header className="account-purchase__backup-head">
        <h3 className="account-purchase__backup-title">Backup codes (-B / -B1 / -B2…)</h3>
        <p className="account-purchase__backup-note">
          Keep for reference only — usually ignore these. Shown separately so they are not mixed into
          the main list ({count}).
        </p>
      </header>
      {children}
    </section>
  );
}

function matchesPurchaseSearch(item: AccountPurchaseItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.item_no.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q) ||
    item.ipg.toLowerCase().includes(q) ||
    item.base_uom.toLowerCase().includes(q) ||
    item.packing.toLowerCase().includes(q) ||
    item.outlet_code.toLowerCase().includes(q) ||
    item.outlet_name.toLowerCase().includes(q)
  );
}

function filterPurchaseSplit(
  split: { primary: AccountPurchaseItem[]; backup: AccountPurchaseItem[] },
  query: string,
) {
  return {
    primary: split.primary.filter((row) => matchesPurchaseSearch(row, query)),
    backup: split.backup.filter((row) => matchesPurchaseSearch(row, query)),
  };
}

export function AccountPurchasePanel({ motherCode, companyName }: AccountPurchasePanelProps) {
  const [subTab, setSubTab] = useState<PurchaseSubTab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AccountPurchaseSummary | null>(null);
  const [sortKey, setSortKey] = useState<PurchaseSortKey>('item_no');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [outletModalItem, setOutletModalItem] = useState<AccountPurchaseItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOutletModalItem(null);
    setSearchQuery('');
    void fetchAccountPurchaseSummary(motherCode)
      .then((summary) => {
        if (cancelled) return;
        setData(summary);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err instanceof Error ? err.message : 'Failed to load purchase history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [motherCode]);

  useEffect(() => {
    setSortKey('item_no');
    setSortDir('asc');
    setSearchQuery('');
    setOutletModalItem(null);
  }, [subTab]);

  const periods = useMemo(
    () => buildPurchasePeriodLabels(data?.meta ?? { mother_code: motherCode }),
    [data?.meta, motherCode],
  );

  const visibleItems = useMemo(
    () => (data ? excludeHiddenPurchaseIpgs(data.items) : []),
    [data],
  );

  const currentMonth = periods.currentMonth;

  const overviewSplit = useMemo(() => {
    const all = aggregatePurchaseItems(visibleItems, currentMonth);
    const { primary, backup } = splitPrimaryBackupItems(all);
    return filterPurchaseSplit(
      {
        primary: sortPurchaseRows(primary, sortKey, sortDir),
        backup: sortPurchaseRows(backup, sortKey, sortDir),
      },
      searchQuery,
    );
  }, [visibleItems, sortKey, sortDir, searchQuery, currentMonth]);

  const alertSplit = useMemo(() => {
    const all = aggregateAlertItems(visibleItems, currentMonth);
    const { primary, backup } = splitPrimaryBackupItems(all);
    return filterPurchaseSplit(
      {
        primary: sortPurchaseRows(primary, sortKey, sortDir),
        backup: sortPurchaseRows(backup, sortKey, sortDir),
      },
      searchQuery,
    );
  }, [visibleItems, sortKey, sortDir, searchQuery, currentMonth]);

  const priceSplit = useMemo(() => {
    const all = aggregatePurchaseItems(visibleItems, currentMonth);
    const { primary, backup } = splitPrimaryBackupItems(all);
    return filterPurchaseSplit(
      {
        primary: sortPurchaseRows(primary, sortKey, sortDir),
        backup: sortPurchaseRows(backup, sortKey, sortDir),
      },
      searchQuery,
    );
  }, [visibleItems, sortKey, sortDir, searchQuery, currentMonth]);

  const alertTabCount = useMemo(() => {
    const all = aggregateAlertItems(visibleItems, currentMonth);
    const { primary, backup } = splitPrimaryBackupItems(all);
    return primary.length + backup.length;
  }, [visibleItems, currentMonth]);

  const modalOutletRows = useMemo(() => {
    if (!outletModalItem) return [];
    return sortPurchaseRows(
      visibleItems.filter((i) => i.item_no === outletModalItem.item_no && isAlertItem(i)),
      'outlet_code',
      'asc',
    );
  }, [visibleItems, outletModalItem]);

  const kpis = useMemo(() => {
    const rows = visibleItems;
    const mainItems = aggregatePurchaseItems(rows, currentMonth);
    return {
      stopped: rows.filter((r) => r.status === 'stopped').length,
      declining: rows.filter((r) => r.status === 'declining').length,
      monthUp: rows.filter((r) => r.month_trend === 'up' || r.month_trend === 'new').length,
      monthDown: rows.filter((r) => r.month_trend === 'down').length,
      outlets: data?.outlets.length ?? 0,
      items: mainItems.length,
    };
  }, [data, visibleItems, currentMonth]);

  function handleSort(key: PurchaseSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(
      key === 'item_no' || key === 'description' || key === 'base_uom' || key === 'outlet_code'
        ? 'asc'
        : 'desc',
    );
  }

  if (loading) {
    return (
      <div className="account-purchase">
        <p className="account-purchase__busy">Loading purchase history…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="account-purchase">
        <p className="account-purchase__error">{error}</p>
        <p className="account-purchase__hint">
          Purchase data is read from PowerBI sales (mother code = account code). Check server
          POWERBI credentials if this persists.
        </p>
      </div>
    );
  }

  if (!data || (data.outlets.length === 0 && data.items.length === 0)) {
    return (
      <div className="account-purchase">
        <p className="account-purchase__empty">
          No outlet / item purchase data found for mother code <strong>{motherCode}</strong>
          {companyName ? ` (${companyName})` : ''}.
        </p>
        <p className="account-purchase__hint">
          CRM account code should match NAV mother outlet. Open the mother account if this is a
          brand / sub-company.
        </p>
      </div>
    );
  }

  return (
    <div className="account-purchase">
      <p className="account-purchase__meta">{periods.legend}</p>

      <div className="account-purchase__kpis" aria-label="Purchase summary">
        <div className="account-purchase__kpi">
          <span className="account-purchase__kpi-label">Outlets</span>
          <strong>{kpis.outlets}</strong>
        </div>
        <div className="account-purchase__kpi">
          <span className="account-purchase__kpi-label">Items</span>
          <strong>{kpis.items}</strong>
        </div>
        <div className="account-purchase__kpi account-purchase__kpi--danger">
          <span className="account-purchase__kpi-label">Stopped</span>
          <strong>{kpis.stopped}</strong>
        </div>
        <div className="account-purchase__kpi account-purchase__kpi--warn">
          <span className="account-purchase__kpi-label">Declining</span>
          <strong>{kpis.declining}</strong>
        </div>
        <div className="account-purchase__kpi">
          <span className="account-purchase__kpi-label">Month est. ↑</span>
          <strong>{kpis.monthUp}</strong>
        </div>
        <div className="account-purchase__kpi">
          <span className="account-purchase__kpi-label">Month est. ↓</span>
          <strong>{kpis.monthDown}</strong>
        </div>
      </div>

      <div className="account-purchase__tabs" role="tablist" aria-label="Purchase views">
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'overview'}
          className={`account-purchase__tab${subTab === 'overview' ? ' account-purchase__tab--active' : ''}`}
          onClick={() => setSubTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'prices'}
          className={`account-purchase__tab${subTab === 'prices' ? ' account-purchase__tab--active' : ''}`}
          onClick={() => setSubTab('prices')}
        >
          Items
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'alerts'}
          className={`account-purchase__tab${subTab === 'alerts' ? ' account-purchase__tab--active' : ''}`}
          onClick={() => setSubTab('alerts')}
        >
          Alerts
          <span className="account-purchase__tab-count">{alertTabCount}</span>
        </button>
      </div>

      <div className="account-purchase__search">
        <input
          type="search"
          className="account-purchase__search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={
            subTab === 'prices'
              ? 'Search item no., description, IPG…'
              : 'Search item no. or description…'
          }
          aria-label="Search purchase items"
        />
        {searchQuery.trim() ? (
          <button
            type="button"
            className="account-purchase__search-clear"
            onClick={() => setSearchQuery('')}
          >
            Clear
          </button>
        ) : null}
      </div>

      <div role="tabpanel" className="account-purchase__panel">
        {subTab === 'overview' ? (
          <>
            <QtyTable
              rows={overviewSplit.primary}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              periods={periods}
            />
            <BackupSection count={overviewSplit.backup.length}>
              <QtyTable
                rows={overviewSplit.backup}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                periods={periods}
              />
            </BackupSection>
          </>
        ) : null}

        {subTab === 'prices' ? (
          <>
            <PricesTable
              rows={priceSplit.primary}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
            />
            <BackupSection count={priceSplit.backup.length}>
              <PricesTable
                rows={priceSplit.backup}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
            </BackupSection>
          </>
        ) : null}

        {subTab === 'alerts' ? (
          <>
            <QtyTable
              rows={alertSplit.primary}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              periods={periods}
              alerts
              allItems={data.items}
              onOpenOutlets={setOutletModalItem}
            />
            {alertSplit.primary.length > 0 ? (
              <p className="account-purchase__hint account-purchase__hint--inline">
                Double-click a row → outlets
              </p>
            ) : null}
            <BackupSection count={alertSplit.backup.length}>
              <QtyTable
                rows={alertSplit.backup}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                periods={periods}
                alerts
                allItems={data.items}
                onOpenOutlets={setOutletModalItem}
              />
            </BackupSection>
          </>
        ) : null}
      </div>

      <Modal
        open={Boolean(outletModalItem)}
        onClose={() => setOutletModalItem(null)}
        title={outletModalItem ? `Outlets · ${outletModalItem.item_no}` : 'Outlets'}
        extraWide
      >
        {outletModalItem ? (
          <div className="account-purchase-modal">
            <header className="account-purchase-modal__header">
              <div className="account-purchase-modal__code">{outletModalItem.item_no}</div>
              <div className="account-purchase-modal__desc">
                {outletModalItem.description || '—'}
              </div>
              <div className="account-purchase-modal__meta">
                <span>Base UOM · {outletModalItem.base_uom || '—'}</span>
                <span>
                  {modalOutletRows.length} outlet{modalOutletRows.length === 1 ? '' : 's'}
                </span>
              </div>
            </header>
            <div className="account-purchase__table-wrap account-purchase__table-wrap--modal">
              <table className="account-purchase__table account-purchase__table--outlets account-purchase__table--qty">
                <thead>
                  <tr>
                    <th>Outlet</th>
                    <th>Name</th>
                    <th>UOM</th>
                    {periods.monthLabels.map((label, index) => (
                      <th
                        key={`omh-${index}`}
                        className={[
                          'account-purchase__num',
                          index + 1 === periods.currentMonth ? 'account-purchase__col-current' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={`${label} ${periods.year}`}
                      >
                        {label}
                      </th>
                    ))}
                    <th className="account-purchase__num account-purchase__col-split">YTD</th>
                    <th>Trend</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {modalOutletRows.map((o) => (
                    <tr
                      key={o.outlet_code}
                      className={
                        o.status === 'stopped'
                          ? 'account-purchase__row--stopped'
                          : o.status === 'declining'
                            ? 'account-purchase__row--declining'
                            : undefined
                      }
                    >
                      <td className="account-purchase__mono">{o.outlet_code}</td>
                      <td className="account-purchase__outlet-name-cell">
                        {o.outlet_name || o.outlet_code}
                      </td>
                      <td className="account-purchase__uom">
                        {o.base_uom || outletModalItem.base_uom || '—'}
                      </td>
                      <QtyCells row={o} periods={periods} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
